import React, { useState, useEffect, useMemo } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineRectangleStack,
  HiOutlineCog,
  HiOutlinePuzzlePiece,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineCheckCircle,
  HiOutlineBuildingOffice2,
  HiOutlineBars3,
  HiOutlineSquares2X2,
} from "react-icons/hi2";
import {
  authService,
  plantService,
  lineService,
  machineService,
  subassemblyService,
  userService,
  masterDataService,
} from "../services";
import { usePermissions } from "../hooks/usePermissions";
import PageLoading from "../components/common/PageLoading";
import SharedModal from "../components/common/SharedModal";
import "./AssetMaster.css";

const AssetMaster = () => {
  const [activeTab, setActiveTab] = useState("lines");
  const [lines, setLines] = useState([]);
  const [machines, setMachines] = useState([]);
  const [subassemblies, setSubassemblies] = useState([]);
  const [plants, setPlants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permissions
  const permissions = {
    lines: {
      view: hasPermission("Line.View"),
      create: hasPermission("Line.Create"),
      edit: hasPermission("Line.Edit"),
      delete: hasPermission("Line.Delete"),
    },
    machines: {
      view: hasPermission("Machine.View"),
      create: hasPermission("Machine.Create"),
      edit: hasPermission("Machine.Edit"),
      delete: hasPermission("Machine.Delete"),
    },
    subassemblies: {
      view: true,
      create: true,
      edit: true,
      delete: true,
    },
  };

  useEffect(() => {
    if (tenantId) {
      loadAllData();
    }
  }, [tenantId]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError("");

      const [
        linesData,
        machinesData,
        subassembliesData,
        plantsData,
        departmentsData,
        usersData,
      ] = await Promise.all([
        lineService.getAllLines(),
        machineService.getAllMachines(),
        subassemblyService.getAllSubassemblies(),
        plantService.getAllPlants(),
        masterDataService.getDepartments(tenantId),
        userService.getAllUsers(),
      ]);

      setLines(linesData || []);
      setMachines(machinesData || []);
      setSubassemblies(subassembliesData || []);
      setPlants(plantsData || []);
      setDepartments(departmentsData || []);
      setUsers(usersData || []);
    } catch (err) {
      console.error("Error loading asset data:", err);
      setError("Failed to load asset data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const currentItems = useMemo(() => {
    let items = [];
    if (activeTab === "lines") items = lines;
    else if (activeTab === "machines") items = machines;
    else if (activeTab === "subassemblies") items = subassemblies;

    if (!searchTerm) return items;

    const term = searchTerm.toLowerCase();
    return items.filter((item) => {
      if (activeTab === "lines") {
        return item.lineName?.toLowerCase().includes(term);
      } else if (activeTab === "machines") {
        return (
          item.machineName?.toLowerCase().includes(term) ||
          item.machineCode?.toLowerCase().includes(term)
        );
      } else if (activeTab === "subassemblies") {
        return (
          item.subassemblyName?.toLowerCase().includes(term) ||
          item.subassemblyCode?.toLowerCase().includes(term)
        );
      }
      return false;
    });
  }, [activeTab, lines, machines, subassemblies, searchTerm]);

  const handleAdd = () => {
    setEditingItem(null);
    let initialPlantId = "";
    if (plants?.length === 1) {
      initialPlantId = plants[0].plantId.toString();
    }

    if (activeTab === "lines") {
      setFormData({
        lineName: "",
        plantId: initialPlantId,
        lineHeadId: "",
      });
    } else if (activeTab === "machines") {
      setFormData({
        machineCode: "",
        machineName: "",
        lineId: "",
        make: "",
        modelNo: "",
        serialNo: "",
        installDate: "",
        userId: "",
        status: "Active",
      });
    } else if (activeTab === "subassemblies") {
      setFormData({
        subassemblyCode: "",
        subassemblyName: "",
        machineId: "",
        status: "Active",
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    if (activeTab === "lines") {
      setFormData({
        lineName: item.lineName || "",
        plantId: item.plantId?.toString() || "",
        lineHeadId: item.lineHeadId?.toString() || "",
      });
    } else if (activeTab === "machines") {
      setFormData({
        machineCode: item.machineCode || "",
        machineName: item.machineName || "",
        lineId: item.lineId?.toString() || "",
        make: item.make || "",
        modelNo: item.modelNo || "",
        serialNo: item.serialNo || "",
        installDate: item.installDate ? item.installDate.split("T")[0] : "",
        userId: item.userId || item.UserId || "",
        status: item.status || "Active",
      });
    } else if (activeTab === "subassemblies") {
      setFormData({
        subassemblyCode: item.subassemblyCode || "",
        subassemblyName: item.subassemblyName || "",
        machineId: item.machineId?.toString() || "",
        status: item.status || "Active",
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (activeTab === "lines") {
      if (!formData.lineName?.trim()) errors.lineName = "Line name is required";
    } else if (activeTab === "machines") {
      if (!formData.machineCode?.trim())
        errors.machineCode = "Machine code is required";
      if (!formData.machineName?.trim())
        errors.machineName = "Machine name is required";
    } else if (activeTab === "subassemblies") {
      if (!formData.subassemblyCode?.trim())
        errors.subassemblyCode = "Subassembly code is required";
      if (!formData.subassemblyName?.trim())
        errors.subassemblyName = "Subassembly name is required";
      if (!formData.machineId) errors.machineId = "Machine is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError("");

      if (activeTab === "lines") {
        const payload = {
          lineName: formData.lineName.trim(),
          plantId: formData.plantId ? parseInt(formData.plantId) : null,
          lineHeadId: formData.lineHeadId
            ? parseInt(formData.lineHeadId)
            : null,
          plant: null,
          department: null,
          machines: [],
        };
        if (editingItem) {
          await lineService.updateLine(editingItem.lineId, {
            ...payload,
            lineId: editingItem.lineId,
          });
        } else {
          await lineService.createLine(payload);
        }
      } else if (activeTab === "machines") {
        const payload = {
          tenantId,
          machineCode: formData.machineCode.trim(),
          machineName: formData.machineName.trim(),
          lineId: formData.lineId ? parseInt(formData.lineId) : null,
          make: formData.make?.trim() || null,
          modelNo: formData.modelNo?.trim() || null,
          serialNo: formData.serialNo?.trim() || null,
          installDate: formData.installDate || null,
          userId: formData.userId ? parseInt(formData.userId) : null,
          status: formData.status,
        };
        if (editingItem) {
          await machineService.updateMachine(editingItem.machineId, {
            ...payload,
            machineId: editingItem.machineId,
          });
        } else {
          await machineService.createMachine(payload);
        }
      } else if (activeTab === "subassemblies") {
        const payload = {
          subassemblyCode: formData.subassemblyCode.trim(),
          subassemblyName: formData.subassemblyName.trim(),
          machineId: parseInt(formData.machineId),
          status: formData.status,
        };
        if (editingItem) {
          await subassemblyService.updateSubassembly(
            editingItem.subassemblyId,
            payload,
          );
        } else {
          await subassemblyService.createSubassembly(payload);
        }
      }

      await loadAllData();
      setShowModal(false);
    } catch (err) {
      console.error("Error saving asset:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to save asset",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      setError("");
      if (activeTab === "lines") await lineService.deleteLine(item.lineId);
      else if (activeTab === "machines")
        await machineService.deleteMachine(item.machineId);
      else if (activeTab === "subassemblies")
        await subassemblyService.deleteSubassembly(item.subassemblyId);

      await loadAllData();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete asset");
    }
  };

  const getPlantName = (id) =>
    plants.find((p) => p.plantId === id)?.plantName || "-";
  const getLineName = (id) =>
    lines.find((l) => l.lineId === id)?.lineName || "-";
  const getMachineName = (id) =>
    machines.find((m) => m.machineId === id)?.machineName || "-";
  const getUserName = (id) =>
    users.find((u) => u.userId == id)?.fullName || "-";

  if (loading && lines.length === 0) {
    return (
      <div className="asset-master-page">
        <PageLoading message="Loading asset data..." />
      </div>
    );
  }

  return (
    <div className="asset-master-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Asset Master</h1>
          <p>Manage your organization's Lines, Machines and Subassemblies</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary btn" onClick={handleAdd}>
            <HiOutlinePlus />
            Add {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}
          </button>
        </div>
      </div>

      <div className="asset-tabs-container">
        <div className="asset-tabs">
          <button
            className={`asset-tab ${activeTab === "lines" ? "active" : ""}`}
            onClick={() => setActiveTab("lines")}
          >
            <HiOutlineRectangleStack /> Lines
            <span className="count-badge">{lines.length}</span>
          </button>
          <button
            className={`asset-tab ${activeTab === "machines" ? "active" : ""}`}
            onClick={() => setActiveTab("machines")}
          >
            <HiOutlineCog /> Machines
            <span className="count-badge">{machines.length}</span>
          </button>
          <button
            className={`asset-tab ${activeTab === "subassemblies" ? "active" : ""}`}
            onClick={() => setActiveTab("subassemblies")}
          >
            <HiOutlinePuzzlePiece /> Subassemblies
            <span className="count-badge">{subassemblies.length}</span>
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container kizantable">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="table-actions-right">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
              >
                <HiOutlineBars3 size={20} />
              </button>
              <button
                className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                <HiOutlineSquares2X2 size={20} />
              </button>
            </div>
            <div className="table-info">
              {currentItems.length} {activeTab}
            </div>
          </div>
        </div>

        {viewMode === "table" ? (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                {activeTab === "lines" && (
                  <tr>
                    <th>ID</th>
                    <th>Line Name</th>
                    <th>Plant</th>
                    <th>Line Head</th>
                    <th>Actions</th>
                  </tr>
                )}
                {activeTab === "machines" && (
                  <tr>
                    <th>Code</th>
                    <th>Machine Name</th>
                    <th>Line</th>
                    <th>Make/Model</th>
                    <th>Assigned User</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                )}
                {activeTab === "subassemblies" && (
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Machine</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {currentItems.map((item, idx) => (
                  <tr key={idx}>
                    {activeTab === "lines" && (
                      <>
                        <td>{idx + 1}</td>
                        <td className="name-cell">{item.lineName}</td>
                        <td>{getPlantName(item.plantId)}</td>
                        <td>{getUserName(item.lineHeadId)}</td>
                      </>
                    )}
                    {activeTab === "machines" && (
                      <>
                        <td className="code-cell">{item.machineCode}</td>
                        <td className="name-cell">{item.machineName}</td>
                        <td>{getLineName(item.lineId)}</td>
                        <td>
                          {item.make} / {item.modelNo}
                        </td>
                        <td>{getUserName(item.userId || item.UserId)}</td>
                        <td>
                          <span
                            className={`status-badge ${item.status?.toLowerCase()}`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </>
                    )}
                    {activeTab === "subassemblies" && (
                      <>
                        <td className="code-cell">{item.subassemblyCode}</td>
                        <td className="name-cell">{item.subassemblyName}</td>
                        <td>{getMachineName(item.machineId)}</td>
                        <td>
                          <span
                            className={`status-badge ${item.status?.toLowerCase()}`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </>
                    )}
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(item)}
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(item)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="asset-grid">
            {currentItems.map((item, idx) => (
              <div className="asset-card" key={idx}>
                <div className="card-header">
                  <div className="asset-type-icon">
                    {activeTab === "lines" && <HiOutlineRectangleStack />}
                    {activeTab === "machines" && <HiOutlineCog />}
                    {activeTab === "subassemblies" && <HiOutlinePuzzlePiece />}
                  </div>
                  <div className="card-actions">
                    <button
                      className="action-btn"
                      onClick={() => handleEdit(item)}
                    >
                      <HiOutlinePencil />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => setDeleteConfirm(item)}
                    >
                      <HiOutlineTrash />
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <span className="asset-code">
                    {item.machineCode ||
                      item.subassemblyCode ||
                      `LN-${idx + 1}`}
                  </span>
                  <h3 className="asset-name">
                    {item.lineName || item.machineName || item.subassemblyName}
                  </h3>
                  <div className="asset-details">
                    {activeTab === "lines" && (
                      <div className="detail-row">
                        <span>Plant: {getPlantName(item.plantId)}</span>
                      </div>
                    )}
                    {activeTab === "machines" && (
                      <div className="detail-row">
                        <span>Line: {getLineName(item.lineId)}</span>
                      </div>
                    )}
                    {activeTab === "subassemblies" && (
                      <div className="detail-row">
                        <span>Machine: {getMachineName(item.machineId)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-footer">
                  <span
                    className={`status-pill ${item.status?.toLowerCase() || "active"}`}
                  >
                    {item.status || "Active"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Standardized Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={`${editingItem ? "Edit" : "Add"} ${activeTab === "lines" ? "Line" : activeTab.slice(0, -1)}`}
        customWidth="800px"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setShowModal(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="asset-form"
              className="btn-primary btn"
              disabled={submitting}
            >
              {submitting ? "Saving..." : editingItem ? "Update" : "Create"}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <form id="asset-form" className="modal-form" onSubmit={handleSubmit}>
            {activeTab === "lines" && (
              <div className="form-section">
                <div className="form-group">
                  <label className="form-label required">Line Name</label>
                  <input
                    type="text"
                    value={formData.lineName}
                    onChange={(e) =>
                      setFormData({ ...formData, lineName: e.target.value })
                    }
                    placeholder="Enter line name"
                    className={formErrors.lineName ? "error" : ""}
                  />
                  {formErrors.lineName && (
                    <span className="field-error">{formErrors.lineName}</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Plant</label>
                  <select
                    value={formData.plantId}
                    onChange={(e) =>
                      setFormData({ ...formData, plantId: e.target.value })
                    }
                  >
                    <option value="">Select Plant</option>
                    {plants.map((p) => (
                      <option key={p.plantId} value={p.plantId}>
                        {p.plantName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Line Head</label>
                  <select
                    value={formData.lineHeadId}
                    onChange={(e) =>
                      setFormData({ ...formData, lineHeadId: e.target.value })
                    }
                  >
                    <option value="">Select user</option>
                    {users.map((u) => (
                      <option key={u.userId} value={u.userId}>
                        {u.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {activeTab === "machines" && (
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Machine Code</label>
                    <input
                      type="text"
                      value={formData.machineCode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          machineCode: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="MCH-001"
                      className={formErrors.machineCode ? "error" : ""}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Machine Name</label>
                    <input
                      type="text"
                      value={formData.machineName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          machineName: e.target.value,
                        })
                      }
                      placeholder="Main Press"
                      className={formErrors.machineName ? "error" : ""}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Line</label>
                  <select
                    value={formData.lineId}
                    onChange={(e) =>
                      setFormData({ ...formData, lineId: e.target.value })
                    }
                  >
                    <option value="">Select Line</option>
                    {lines.map((l) => (
                      <option key={l.lineId} value={l.lineId}>
                        {l.lineName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Make</label>
                    <input
                      type="text"
                      value={formData.make}
                      onChange={(e) =>
                        setFormData({ ...formData, make: e.target.value })
                      }
                      placeholder="Siemens"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Model No</label>
                    <input
                      type="text"
                      value={formData.modelNo}
                      onChange={(e) =>
                        setFormData({ ...formData, modelNo: e.target.value })
                      }
                      placeholder="S7-1200"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Assigned User</label>
                  <select
                    value={formData.userId}
                    onChange={(e) =>
                      setFormData({ ...formData, userId: e.target.value })
                    }
                  >
                    <option value="">Select user</option>
                    {users.map((u) => (
                      <option key={u.userId} value={u.userId}>
                        {u.fullName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Maintenance">Maintenance</option>
                  </select>
                </div>
              </div>
            )}

            {activeTab === "subassemblies" && (
              <div className="form-section">
                <div className="form-group">
                  <label className="form-label required">Subassembly Code</label>
                  <input
                    type="text"
                    value={formData.subassemblyCode}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        subassemblyCode: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="SUB-001"
                    className={formErrors.subassemblyCode ? "error" : ""}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Subassembly Name</label>
                  <input
                    type="text"
                    value={formData.subassemblyName}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        subassemblyName: e.target.value,
                      })
                    }
                    placeholder="Enter name"
                    className={formErrors.subassemblyName ? "error" : ""}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Machine</label>
                  <select
                    value={formData.machineId}
                    onChange={(e) =>
                      setFormData({ ...formData, machineId: e.target.value })
                    }
                    className={formErrors.machineId ? "error" : ""}
                  >
                    <option value="">Select Machine</option>
                    {machines.map((m) => (
                      <option key={m.machineId} value={m.machineId}>
                        {m.machineName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            )}
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Delete
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>
              {deleteConfirm?.lineName ||
                deleteConfirm?.machineName ||
                deleteConfirm?.subassemblyName}
            </strong>?
          </p>
          <p className="warning-text" style={{color: 'var(--status-error)', marginTop: '10px'}}>
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default AssetMaster;
